"use client";

import { useState, useRef, useCallback, ComponentType, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useStyles } from "./styles";
import MonumetricAd from "@/components/adverts/MonumetricAd";
import Button from "@/components/common/PrimaryButton";

interface ListItemBase {
  id: number;
}

interface InfiniteScrollProps<
  T extends ListItemBase,
  ExtraProps = Record<string, any>
> {
  list: T[];
  listItem: ComponentType<Omit<T, keyof ExtraProps> & ExtraProps>;
  displayIncrements?: { mobile: number; desktop: number };
  onClick?: (id: number) => void;
  showAds?: boolean;
  adFrequency?: number;
  className?: string;
  componentProps?: Partial<ExtraProps>;
  entryIndex?: number;
}

const InfiniteScroll = <
  T extends ListItemBase,
  ExtraProps = Record<string, any>
>({
  list,
  listItem: ListItemComponent,
  displayIncrements = { mobile: 3, desktop: 6 },
  onClick = (id: number) => {},
  showAds = false,
  adFrequency = 0,
  className,
  componentProps = {} as Partial<T> & ExtraProps & Record<string, any>,
  entryIndex = 0,
}: InfiniteScrollProps<T, ExtraProps>) => {
  const { classes, cx } = useStyles();
  const isLargeScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("lg")
  );
  const displayIncrement = isLargeScreen
    ? displayIncrements.desktop
    : displayIncrements.mobile;
  const adInterval = adFrequency || displayIncrement;

  const [startIndex, setStartIndex] = useState(
    list.length <= displayIncrement ? 0 : entryIndex
  );
  const [endIndex, setEndIndex] = useState(entryIndex + displayIncrement);

  const displayedList = list.slice(startIndex, endIndex);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerBottom = useRef<IntersectionObserver | null>(null);
  const previousListLengthRef = useRef(list.length);
  const hasInitializedRef = useRef(false);

  const handleViewPrevious = () => {
    setStartIndex((prevStartIndex) =>
      Math.max(prevStartIndex - displayIncrement, 0)
    );
  };

  useEffect(() => {
    if (!hasInitializedRef.current) {
      setStartIndex(list.length <= displayIncrement ? 0 : entryIndex);
      setEndIndex(entryIndex + displayIncrement);
      hasInitializedRef.current = true;
      previousListLengthRef.current = list.length;
    } else if (list.length !== previousListLengthRef.current) {
      const lengthDiff = list.length - previousListLengthRef.current;
      if (lengthDiff > 0) {
        setEndIndex((prevEndIndex) => prevEndIndex + lengthDiff);
      }
      previousListLengthRef.current = list.length;
    }
  }, [list.length, displayIncrement, entryIndex]);

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || list.length === 0 || endIndex === list.length) return;

      if (observerBottom.current) observerBottom.current.disconnect();
      observerBottom.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setEndIndex((prevEndIndex) =>
            Math.min(prevEndIndex + displayIncrement, list.length)
          );
        }
      });
      if (node) observerBottom.current.observe(node);
    },
    [endIndex, list.length, displayIncrement]
  );

  if (list.length === 0) return null;

  return (
    <div className={cx(classes.container, className)} ref={containerRef}>
      {startIndex > 0 && (
        <Button
          variant="outlined"
          onClick={handleViewPrevious}
          className={classes.loadPreviousButton}
        >
          Load Previous
        </Button>
      )}
      {displayedList.map((item, index) => {
        const isLastItem = index === displayedList.length - 1;
        const ref = (componentProps as any).generationRefs?.[item.id];

        return (
          <div
            key={`list-item-${startIndex + index}`}
            ref={isLastItem ? lastElementRef : null}
          >
            <ListItemComponent
              {...item}
              {...(componentProps as Partial<T> & ExtraProps)}
              ref={ref}
              onClick={() => onClick(item.id)}
            />
            {/* {showAds &&
              ((index !== 0 && index % adInterval === 0) ||
                adInterval === 1) && (
                <div className={classes.adContainer}>
                  <MonumetricAd variant="in-content-repeatable" />
                </div>
              )} */}
          </div>
        );
      })}
    </div>
  );
};

export default InfiniteScroll;
